import { Box, Card, CardBody, ChakraProvider, Container, Divider, Heading, Link, List, ListItem, Text, useColorModeValue, VStack } from "@chakra-ui/react";
import React from "react";

const TermsConditionsPage = () => {
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.700", "gray.300");
  const brandColor = "#CF642A";

  return (
    <ChakraProvider>
      <Box bg={bgColor} minHeight="100vh" py={8}>
        <Container maxW="5xl">
          <VStack spacing={6} align="stretch">
            {/* Header */}
            <Box textAlign="center" py={6}>
              <Heading size="xl" color={brandColor} mb={4}>
                Terms and Conditions for EV Charging Service
              </Heading>
              <Text fontSize="lg" color={textColor}>
                Welcome to the free EV charging service ("Service"). Please read these Terms and Conditions carefully before using the Service. By accessing or using the Service, you agree to be bound by these terms. If you do not agree, please do not use the Service.
              </Text>
            </Box>

            {/* Main Content */}
            <Card bg={cardBg} shadow="lg">
              <CardBody p={8}>
                <VStack spacing={6} align="stretch">
                  {/* Section 1 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      1. Eligibility
                    </Heading>
                    <List spacing={2} ml={4}>
                      <ListItem>
                        <Text color={textColor}>• The Service is available to registered users who comply with these Terms.</Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>• The Service is intended for personal, non-commercial use only.</Text>
                      </ListItem>
                    </List>
                  </Box>

                  <Divider />

                  {/* Section 2 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      2. Usage of the Service
                    </Heading>
                    <List spacing={2} ml={4}>
                      <ListItem>
                        <Text color={textColor}>• The Service provides free electric vehicle (EV) charging at designated charging stations.</Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>• Users must follow all instructions provided at the charging stations.</Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>• Users are responsible for connecting and disconnecting their vehicles safely and correctly.</Text>
                      </ListItem>
                    </List>
                  </Box>

                  <Divider />

                  {/* Section 3 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      3. User Responsibilities
                    </Heading>
                    <List spacing={2} ml={4}>
                      <ListItem>
                        <Text color={textColor}>• You agree to use the charging stations responsibly and to not cause damage or harm.</Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>• You must adhere to all safety guidelines and local laws during charging.</Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>• Users are responsible for ensuring their vehicle is compatible with the charging station.</Text>
                      </ListItem>
                    </List>
                  </Box>

                  <Divider />

                  {/* Section 4 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      4. Service Limitations
                    </Heading>
                    <List spacing={2} ml={4}>
                      <ListItem>
                        <Text color={textColor}>• The Service is provided "as is" without warranties of any kind.</Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>• We do not guarantee the availability of charging stations at all times.</Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>• The speed and duration of charging may vary depending on equipment and conditions.</Text>
                      </ListItem>
                    </List>
                  </Box>

                  <Divider />

                  {/* Section 5 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      5. Prohibited Activities
                    </Heading>
                    <List spacing={2} ml={4}>
                      <ListItem>
                        <Text color={textColor}>• Do not tamper with or disable the charging stations.</Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>• Do not use the Service for illegal activities.</Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>• Do not interfere with the operation of the Service or other users' vehicles.</Text>
                      </ListItem>
                    </List>
                  </Box>

                  <Divider />

                  {/* Section 6 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      6. Liability
                    </Heading>
                    <List spacing={2} ml={4}>
                      <ListItem>
                        <Text color={textColor}>• We are not responsible for any damage or loss resulting from the use of the Service.</Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>• We are not liable for any injury or accident caused during charging.</Text>
                      </ListItem>
                    </List>
                  </Box>

                  <Divider />

                  {/* Section 7 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      7. Modifications and Termination
                    </Heading>
                    <List spacing={2} ml={4}>
                      <ListItem>
                        <Text color={textColor}>• We reserve the right to modify or terminate the Service at any time without prior notice.</Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>• We may suspend or restrict access for violations of these Terms.</Text>
                      </ListItem>
                    </List>
                  </Box>

                  <Divider />

                  {/* Section 8 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      8. Privacy
                    </Heading>
                    <Text color={textColor} ml={4}>
                      • Your use of the Service is also governed by our{" "}
                      <Link href="/privacy-policy" color={brandColor} fontWeight="semibold">
                        Privacy Policy
                      </Link>
                      .
                    </Text>
                  </Box>

                  <Divider />

                  {/* Section 9 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      9. Changes to Terms
                    </Heading>
                    <Text color={textColor} ml={4}>
                      • We may update these Terms periodically. Continued use of the Service signifies acceptance of any changes.
                    </Text>
                  </Box>

                  <Divider />

                  {/* Section 10 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      10. Contact Us
                    </Heading>
                    <Text color={textColor} ml={4}>
                      For any questions about these Terms and Conditions, please contact:
                    </Text>
                    <VStack align="start" ml={8} spacing={1}>
                      <Text color={textColor}>
                        • <strong>Owner:</strong> Cody
                      </Text>
                      <Text color={textColor}>
                        • <strong>Organization:</strong> VET EV Charging Station
                      </Text>
                      <Text color={textColor}>
                        • <strong>Email:</strong>{" "}
                        <Link href="mailto:lakinakim0@gmail.com" color={brandColor}>
                          lakinakim0@gmail.com
                        </Link>
                      </Text>
                      <Text color={textColor}>
                        • <strong>Phone:</strong>{" "}
                        <Link href="tel:+6069307501" color={brandColor}>
                          +6069307501
                        </Link>
                      </Text>
                    </VStack>
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          </VStack>
        </Container>
      </Box>
    </ChakraProvider>
  );
};

export default TermsConditionsPage;
